import { ReactNode } from "react";
import { AuroraMesh } from "@/components/dashboard/AuroraMesh";
import { ConstellationField } from "@/components/dashboard/ConstellationField";
import { ParallaxLayer } from "@/components/motion/ParallaxLayer";
import { cn } from "@/lib/utils";

interface HeroAuroraProps {
  children: ReactNode;
  /** Show the constellation dot field overlay. */
  constellation?: boolean;
  /** Aurora intensity 0..1. Default 0.55. */
  intensity?: number;
  /** Optional className for the outer section. */
  className?: string;
  /** Optional className for the inner content wrapper. */
  innerClassName?: string;
}

/**
 * Marketing hero wrapper combining AuroraMesh + ConstellationField + ParallaxLayer.
 * Drop-in replacement for hero `<section>` blocks on public pages.
 *
 * Real H1/H2 stay inside `children` so SEO is untouched.
 */
export function HeroAurora({
  children,
  constellation = true,
  intensity = 0.55,
  className,
  innerClassName,
}: HeroAuroraProps) {
  return (
    <section
      className={cn(
        "relative overflow-hidden isolate",
        className
      )}
    >
      {/* Aurora layer */}
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ opacity: intensity }}>
        <AuroraMesh />
      </div>
      {/* Constellation overlay */}
      {constellation && (
        <div aria-hidden className="absolute inset-0 pointer-events-none opacity-60">
          <ParallaxLayer speed={0.18}>
            <div className="relative w-full h-[420px] md:h-[560px]">
              <ConstellationField count={42} />
            </div>
          </ParallaxLayer>
        </div>
      )}
      {/* Soft veil so foreground text remains readable */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, hsl(var(--background) / 0.15) 0%, hsl(var(--background) / 0.55) 60%, hsl(var(--background)) 100%)",
        }}
      />
      <div className={cn("relative", innerClassName)}>{children}</div>
    </section>
  );
}
